import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Download, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = ["Comisión", "Transferencia", "Banco", "Operador", "Error operativo", "Préstamo", "Otro"];

const Row = ({ label, value, currency, accent }) => (
  <div className="flex justify-between items-baseline border-b border-slate-100 py-2">
    <span className="text-sm text-slate-600">{label}</span>
    <span className={`font-mono tabular-nums font-semibold ${
      accent === "green" ? "text-emerald-600" : accent === "red" ? "text-rose-600" : accent === "amber" ? "text-amber-600" : "text-slate-900"
    }`}>
      {value} {currency && <span className="text-xs text-slate-500">{currency}</span>}
    </span>
  </div>
);

export default function GastosMayor({ embedded = false }) {
  const { isAdmin } = useAuth();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [openCash, setOpenCash] = useState(false);
  const [cashForm, setCashForm] = useState({ amount: 0, memo: "" });
  const [openPay, setOpenPay] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, note: "", category: "Otro", date: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      api.get("/major/monthly-summary", { params: { month, year } }),
      api.get("/major/payments", { params: { month, year } }),
    ]);
    setSummary(s.data); setPayments(p.data);
    setCashForm({ amount: s.data.initial_cash_usd, memo: "" });
  }, [month, year]);
  useEffect(() => { load(); }, [load]);

  const saveCash = async () => {
    try { await api.post("/major/initial-cash", { month, year, amount: parseFloat(cashForm.amount), memo: cashForm.memo }); toast.success("Saldo inicial guardado"); setOpenCash(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addPayment = async () => {
    try {
      await api.post("/major/payments", { month, year, amount: parseFloat(payForm.amount), note: payForm.note, category: payForm.category, date: new Date(payForm.date).toISOString() });
      toast.success("Pago/gasto agregado"); setOpenPay(false); setPayForm({ amount: 0, note: "", category: "Otro", date: new Date().toISOString().slice(0, 10) }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delPayment = async (id) => {
    if (!confirm("¿Eliminar este pago/gasto?")) return;
    try { await api.delete(`/major/payments/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API}/reports/major-monthly?month=${month}&year=${year}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `capital_mayor_${year}_${month}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="gastos-mayor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {!embedded && <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Gastos &amp; Capital Mayor</h1>}
          <p className="text-slate-500 mt-1">Resumen mensual: efectivo USD, USDT y capital estimado</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-major"><Download size={16} className="mr-2" /> Exportar</Button>
          {isAdmin && <Button onClick={() => setOpenCash(true)} className="bg-slate-900" data-testid="set-initial-cash"><Save size={16} className="mr-2" /> Saldo inicial</Button>}
          {isAdmin && <Button onClick={() => setOpenPay(true)} className="bg-rose-600 hover:bg-rose-700" data-testid="new-payment"><Plus size={16} className="mr-2" /> Pago/Gasto</Button>}
        </div>
      </div>

      <Card className="p-4 border border-slate-200 rounded-xl bg-white flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-xs uppercase font-semibold text-slate-500">Mes</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="major-month"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 12 }).map((_, i) => (<SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold text-slate-500">Año</Label>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || today.getFullYear())} className="w-28" data-testid="major-year" />
        </div>
      </Card>

      {summary && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 border border-slate-200 rounded-xl bg-white">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-3">Resumen Mayor Mensual</h3>
            <Row label="Inicio USD efectivo" value={fmt.usdt(summary.initial_cash_usd)} currency="USD" />
            <Row label="Total pagado por compras" value={fmt.usdt(summary.total_buy_paid_usd)} currency="USD" />
            <Row label="Total recibido por ventas" value={fmt.usdt(summary.total_sell_received_usd)} currency="USD" />
            <Row label="Pagos/Gastos" value={fmt.usdt(summary.total_payments)} currency="USD" accent="amber" />
            <Row label="Beneficio bruto" value={fmt.usdt(summary.gross_profit)} currency="USD" accent={summary.gross_profit >= 0 ? "green" : "red"} />
            <Row label="Balance general" value={fmt.usdt(summary.general_balance)} currency="USD" accent={summary.general_balance >= 0 ? "green" : "red"} />
          </Card>

          <Card className="p-6 border border-slate-200 rounded-xl bg-white">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-3">Saldos disponibles</h3>
            <Row label="Saldo disponible USDT (inventario)" value={fmt.usdt(summary.available_usdt_balance)} currency="USDT" accent="green" />
            <Row label="Efectivo USD disponible (caja)" value={fmt.usdt(summary.available_cash_usd)} currency="USD" accent="green" />
            <Row label="Valor estimado del USDT (× costo prom)" value={fmt.usdt(summary.estimated_usdt_value)} currency="USD" />
            <Row label="Capital estimado disponible" value={fmt.usdt(summary.estimated_available_capital)} currency="USD" accent="green" />
            <p className="text-xs text-slate-500 mt-3">El saldo disponible USDT no se mezcla con el efectivo USD. Cuadre por separado contra wallet/caja.</p>
          </Card>
        </div>
      )}

      <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Pagos / Gastos del mes</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-right font-semibold">Monto</th>
                <th className="px-4 py-3 text-left font-semibold">Categoría</th>
                <th className="px-4 py-3 text-left font-semibold">Nota</th>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {payments.length === 0 && (<tr><td colSpan={6} className="text-center py-8 text-slate-400">Sin pagos en este mes</td></tr>)}
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-600">{fmt.date(p.date)}</td>
                  <td className="px-4 py-3 text-right text-rose-600 font-semibold">{fmt.usdt(p.amount)}</td>
                  <td className="px-4 py-3 font-sans">{p.category}</td>
                  <td className="px-4 py-3 font-sans text-slate-600">{p.note}</td>
                  <td className="px-4 py-3 font-sans text-slate-600">{p.created_by_name}</td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && <button onClick={() => delPayment(p.id)} data-testid={`del-pay-${p.id}`} className="p-1.5 rounded hover:bg-rose-100 text-rose-600"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              ))}
              {payments.length > 0 && (
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td className="px-4 py-3 font-bold">Total</td>
                  <td className="px-4 py-3 text-right font-bold text-rose-700">{fmt.usdt(summary?.total_payments || 0)}</td>
                  <td colSpan={4}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={openCash} onOpenChange={setOpenCash}>
        <DialogContent data-testid="cash-dialog">
          <DialogHeader><DialogTitle>Saldo inicial USD efectivo del mes</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Monto USD efectivo</Label><Input type="number" step="0.01" value={cashForm.amount} onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })} data-testid="cash-amount" /></div>
            <div><Label>Memo</Label><Textarea value={cashForm.memo} onChange={(e) => setCashForm({ ...cashForm, memo: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCash(false)}>Cancelar</Button>
            <Button onClick={saveCash} className="bg-slate-900" data-testid="save-cash">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openPay} onOpenChange={setOpenPay}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Nuevo pago/gasto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Fecha</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} data-testid="pay-date" /></div>
            <div><Label>Monto USD</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount" /></div>
            <div>
              <Label>Categoría</Label>
              <Select value={payForm.category} onValueChange={(v) => setPayForm({ ...payForm, category: v })}>
                <SelectTrigger data-testid="pay-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Nota</Label><Textarea value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="pay-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPay(false)}>Cancelar</Button>
            <Button onClick={addPayment} className="bg-rose-600" data-testid="save-payment">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
